import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { 
  DollarSign, 
  CreditCard, 
  TrendingUp, 
  TrendingDown,
  Users,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  BarChart3,
  PieChart,
  FileText,
  Download,
  Eye,
  Settings,
  Calendar,
  Filter,
  Plus,
  Edit
} from 'lucide-react';
import { useDataContext } from '../../../contexts/DataContext';
import Chart from '../../../components/ui/Chart';
import { useNavigate } from 'react-router-dom';

const BillingDashboard = () => {
  const navigate = useNavigate();
  const { getMockData } = useDataContext();
  const [revenueMetrics, setRevenueMetrics] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadBillingData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const data = getMockData('billingData');
      setRevenueMetrics(data.revenueMetrics || {});
      setSubscriptions(data.subscriptions || []);
      setPayments(data.payments || []);
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [getMockData]);

  useEffect(() => {
    loadBillingData();
  }, [loadBillingData]);

  const handleRefresh = () => {
    loadBillingData();
  };

  const handleBillingAction = (action, id) => {
    // Simulate billing action
    alert(`Billing action: ${action} for ${id}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'green';
      case 'cancelled': return 'red';
      case 'pending': return 'yellow';
      case 'overdue': return 'red';
      case 'paid': return 'green';
      case 'failed': return 'red';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return CheckCircle;
      case 'cancelled': return AlertTriangle;
      case 'pending': return Clock;
      case 'overdue': return AlertTriangle;
      case 'paid': return CheckCircle;
      case 'failed': return AlertTriangle;
      default: return Clock;
    }
  };

  const revenueMetricsCards = [
    {
      title: 'Monthly Revenue',
      value: `$${revenueMetrics?.monthlyRevenue || 0}`,
      icon: DollarSign,
      color: 'green',
      trend: '+12%',
      trendDirection: 'up',
      subtitle: 'vs last month'
    },
    {
      title: 'Active Subscriptions',
      value: revenueMetrics?.activeSubscriptions || 0,
      icon: Users,
      color: 'blue',
      trend: '+8%',
      trendDirection: 'up',
      subtitle: 'vs last month'
    },
    {
      title: 'Payment Success Rate',
      value: `${revenueMetrics?.paymentSuccessRate || 0}%`,
      icon: CreditCard,
      color: 'green',
      trend: '+2%',
      trendDirection: 'up',
      subtitle: 'vs last month'
    },
    {
      title: 'Overdue Invoices',
      value: revenueMetrics?.overdueInvoices || 0,
      icon: AlertTriangle,
      color: 'red',
      trend: '-5%',
      trendDirection: 'down',
      subtitle: 'vs last month'
    }
  ];

  const revenueData = [
    { name: 'Jan', revenue: 45000, subscriptions: 120 },
    { name: 'Feb', revenue: 52000, subscriptions: 135 },
    { name: 'Mar', revenue: 48000, subscriptions: 128 },
    { name: 'Apr', revenue: 61000, subscriptions: 145 },
    { name: 'May', revenue: 58000, subscriptions: 142 },
    { name: 'Jun', revenue: 67000, subscriptions: 158 }
  ];

  const subscriptionPlansData = [
    { name: 'Basic', value: revenueMetrics?.basicPlanCount || 0, color: '#3B82F6' },
    { name: 'Professional', value: revenueMetrics?.professionalPlanCount || 0, color: '#10B981' },
    { name: 'Enterprise', value: revenueMetrics?.enterprisePlanCount || 0, color: '#F59E0B' }
  ];

  const recentPayments = payments.slice(0, 5).map(payment => ({
    id: payment.id,
    customer: payment.customer,
    amount: payment.amount,
    status: payment.status,
    date: payment.date,
    method: payment.method
  }));

  return (
    <div className="billing-dashboard p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Billing Dashboard</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Monitor financial performance and revenue data</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-4">
            <Button onClick={handleRefresh} disabled={isRefreshing} variant="outline">
              {isRefreshing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </>
              )}
            </Button>
            <Button onClick={() => navigate('/super-admin/billing/invoices/new')}>
              <Plus className="h-4 w-4 mr-2" />
              New Invoice
            </Button>
          </div>
        </div>
      </div>

      {/* Revenue Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {revenueMetricsCards.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">{metric.title}</CardTitle>
                <Icon className={`h-4 w-4 text-${metric.color}-500`} />
              </CardHeader>
              <CardContent>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">{metric.value}</div>
                <div className={`flex items-center text-xs ${
                  metric.trendDirection === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {metric.trendDirection === 'up' ? (
                    <TrendingUp className="h-3 w-3 mr-1" />
                  ) : (
                    <TrendingDown className="h-3 w-3 mr-1" />
                  )}
                  {metric.trend} {metric.subtitle}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {/* Revenue Trends */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Revenue Trends (6 Months)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Chart 
              type="line" 
              data={revenueData} 
              height={300}
              colors={['#10B981', '#3B82F6']}
            />
          </CardContent>
        </Card>

        {/* Subscription Plans Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <PieChart className="h-5 w-5 mr-2" />
              Subscription Plans Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Chart type="pie" data={subscriptionPlansData} height={300} />
          </CardContent>
        </Card>
      </div>

      {/* Recent Payments */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <CreditCard className="h-5 w-5 mr-2" />
              Recent Payments
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/super-admin/billing/payments')}
            >
              <Eye className="h-4 w-4 mr-1" />
              View All
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentPayments.map((payment) => {
              const StatusIcon = getStatusIcon(payment.status);
              return (
                <div key={payment.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <CreditCard className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{payment.customer}</h3>
                      <p className="text-sm text-gray-600">{payment.method} • {new Date(payment.date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-lg font-semibold">${payment.amount}</p>
                      <div className="flex items-center space-x-1">
                        <StatusIcon className={`h-4 w-4 text-${getStatusColor(payment.status)}-500`} />
                        <Badge variant={payment.status === 'paid' ? 'default' : 'secondary'}>
                          {payment.status}
                        </Badge>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Subscription Management */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Subscription Management
            </div>
          <div className="flex space-x-2">
            <Button 
              variant="default"
              size="sm"
              onClick={() => navigate('/super-admin/billing/subscriptions')}
              className="gradient-primary text-primary-foreground"
            >
              View All Subscriptions →
            </Button>
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-1" />
              Filter
            </Button>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
          </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subscriptions.slice(0, 6).map((subscription) => {
              return (
                <div key={subscription.id} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 font-semibold text-sm">
                          {subscription.customer.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{subscription.customer}</h3>
                        <p className="text-sm text-gray-600">{subscription.plan}</p>
                      </div>
                    </div>
                    <Badge variant={subscription.status === 'active' ? 'default' : 'secondary'}>
                      {subscription.status}
                    </Badge>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Amount:</span>
                      <span className="font-semibold">${subscription.amount}/month</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Next Billing:</span>
                      <span>{new Date(subscription.nextBilling).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Users:</span>
                      <span>{subscription.users}</span>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleBillingAction('View Details', subscription.id)}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      View
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleBillingAction('Edit Subscription', subscription.id)}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleBillingAction('Generate Invoice', subscription.id)}
                    >
                      <FileText className="h-3 w-3 mr-1" />
                      Invoice
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Settings className="h-5 w-5 mr-2" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Button 
              className="h-20 flex flex-col items-center justify-center space-y-2 gradient-primary text-primary-foreground"
              onClick={() => navigate('/super-admin/billing/invoices')}
            >
              <Plus className="h-6 w-6" />
              <span>Create Invoice</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex flex-col items-center justify-center space-y-2 hover:bg-primary hover:text-primary-foreground"
              onClick={() => navigate('/super-admin/billing/payments')}
            >
              <CreditCard className="h-6 w-6" />
              <span>Process Payment</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex flex-col items-center justify-center space-y-2 hover:bg-primary hover:text-primary-foreground"
              onClick={() => navigate('/super-admin/billing/reports')}
            >
              <BarChart3 className="h-6 w-6" />
              <span>Revenue Report</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex flex-col items-center justify-center space-y-2 hover:bg-primary hover:text-primary-foreground"
              onClick={() => navigate('/super-admin/billing/subscriptions')}
            >
              <Calendar className="h-6 w-6" />
              <span>Manage Subscriptions</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingDashboard;
